import React, { useEffect, useRef, useState } from "react";
import {
  deleteLocation,
  fetchCountries,
  fetchFromEbayLocationSettings,
  fetchLocationSettings,
  fetchLocationTypes,
  updateLocation,
} from "../../../api/rest/ebay";
import Loader from "../../../components/generic/Loader";
import EbayAddressForm from "../../../components/integrations/EbayAddressForm";
import EbayDeleteSetting from "../../../components/integrations/EbayDeleteSetting";
import EbayErrorComponent from "../../../components/integrations/EbayErrorComponent";
import EbaySettingsList from "../../../components/integrations/EbaySettingsList";
import { useStoreDetails } from "../../../hooks/storeHooks";
import { useQueryState } from "../../../hooks/stateHooks";
import { parseEbayErrorMessage } from "../../../utils/integrations";
import { segmentAnalytics } from "../../../components/services/Analytics";
import { SAVE_POLICY_CLICKED } from "../../../constants/eventsTracked";

const LOCATION_SETTINGS = "Location Settings";

const wrapTitle = (children: React.ReactChild, errors: string[]) => (
  <>
    <h2>{LOCATION_SETTINGS}</h2>
    <EbayErrorComponent errors={errors} />
    {children}
  </>
);

function LocationSettingsContainer() {
  const { customerId } = useStoreDetails();
  const listRef = useRef<HTMLElement>();
  const [countries, setCountries] = useState<EbayParam[]>([]);
  const [locationTypes, setLocationTypes] = useState<EbayParam[]>([]);
  const [settings, setSettings] = useState<EbayLocation[]>([]);
  const [editSetting, setEditSetting] = useQueryState<number | null>(
    "edit",
    null
  );
  const [deletingSetting, setDeletingSetting] = useQueryState<number | null>(
    "delete",
    null
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  const loadSettings = () => {
    setIsLoading(true);
    fetchLocationSettings()
      .then((data: EbayLocation[]) => setSettings(data))
      .catch((error: Error) => setErrors(parseEbayErrorMessage(error)))
      .finally(() => setIsLoading(false));
  };

  const refreshSettings = () => {
    setIsLoading(true);
    fetchFromEbayLocationSettings()
      .then((data: EbayLocation[]) => setSettings(data))
      .catch((error: Error) => setErrors(parseEbayErrorMessage(error)))
      .finally(() => setIsLoading(false));
  };

  const saveSetting = (values: EbayLocation, setSubmitting: Function) => {
    if (values.id < 0) values.id = null;
    setSubmitting(true);
    updateLocation(values)
      .then(() => {
        segmentAnalytics.track(SAVE_POLICY_CLICKED, {
          integration_type: "ebay",
          policy_type: LOCATION_SETTINGS,
        });
        loadSettings();
        setEditSetting(null);
      })
      .catch((error: Error) => setErrors(parseEbayErrorMessage(error)))
      .finally(() => setSubmitting(false));
  };

  const deleteSetting = (settingId: number) => {
    setIsDeleting(true);
    deleteLocation(settingId)
      .then(() => loadSettings())
      .catch((error: Error) => setErrors(parseEbayErrorMessage(error)))
      .finally(() => {
        setIsDeleting(false);
        setDeletingSetting(null);
      });
  };

  const loadCountries = () =>
    fetchCountries()
      .then((data: EbayParam[]) => setCountries(data))
      .catch((error: Error) => setErrors(parseEbayErrorMessage(error)));

  const loadLocationTypes = () =>
    fetchLocationTypes()
      .then((data: EbayParam[]) => setLocationTypes(data))
      .catch((error: Error) => setErrors(parseEbayErrorMessage(error)));

  useEffect(() => {
    loadLocationTypes();
    loadCountries();
    if (customerId) {
      loadSettings();
    }
  }, [customerId]);

  useEffect(() => {
    setErrors([]);
  }, [editSetting, deletingSetting]);

  if (isLoading) {
    return wrapTitle(<Loader />, errors);
  }

  if (editSetting) {
    return wrapTitle(
      <EbayAddressForm
        countries={countries}
        locationTypes={locationTypes}
        location={settings.find((setting) => setting.id === editSetting)}
        setEditSetting={() => setEditSetting(null)}
        saveSetting={saveSetting}
      />,
      errors
    );
  }

  if (deletingSetting) {
    const location = settings.find((setting) => setting.id === deletingSetting);
    if (location) {
      const { id, name } = location;
      return wrapTitle(
        <EbayDeleteSetting
          id={id}
          name={name}
          isDeleting={isDeleting}
          handleCancel={() => setDeletingSetting(null)}
          handleDelete={(id: number) => deleteSetting(id)}
        />,
        errors
      );
    }
  }

  return wrapTitle(
    <EbaySettingsList
      settingsTitle={LOCATION_SETTINGS}
      settings={settings}
      deletingSetting={deletingSetting}
      refreshSettings={refreshSettings}
      setEditSetting={setEditSetting}
      setDeletingSetting={setDeletingSetting}
      listRef={listRef}
    />,
    errors
  );
}

export default LocationSettingsContainer;
